import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './useAuth';

/**
 * Hook for managing deal activities (unified activity tracking)
 * Uses the new 'dealActivities' collection which combines:
 * - Emails (linked from Gmail)
 * - Calendar events (linked from Google Calendar)
 * - Notes (manual)
 * - Calls (manual)
 * - Meetings (manual)
 *
 * @param {string} dealId - The deal ID to fetch activities for (required)
 * @param {string} typeFilter - Optional filter by activity type
 */
export function useActivities(dealId = null, typeFilter = null) {
  const { currentUser } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Real-time listener for dealActivities collection
  useEffect(() => {
    if (!currentUser || !dealId) {
      setActivities([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Build query - always filter by ownerId and dealId
    let q = query(
      collection(db, 'dealActivities'),
      where('ownerId', '==', currentUser.uid),
      where('dealId', '==', dealId),
      orderBy('activityDate', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        let activitiesData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            // Map to legacy format for backwards compatibility
            timestamp: data.activityDate,
            content: data.description || '',
          };
        });

        // Apply type filter client-side if specified
        if (typeFilter) {
          activitiesData = activitiesData.filter(a => a.type === typeFilter);
        }

        setActivities(activitiesData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching activities:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser, dealId, typeFilter]);

  // Add new activity (note, call, meeting)
  const addActivity = async (activityData) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to add activities');
    }

    if (!activityData.dealId) {
      throw new Error('dealId is required');
    }

    try {
      const now = serverTimestamp();

      const docRef = await addDoc(collection(db, 'dealActivities'), {
        ownerId: currentUser.uid,
        dealId: activityData.dealId,
        type: activityData.type || 'note',
        title: activityData.title || '',
        description: activityData.content || activityData.description || '',
        activityDate: now,
        createdAt: now,
        updatedAt: now,
        createdBy: 'manual',
        metadata: {
          linkedBy: 'manual',
        },
      });
      return docRef.id;
    } catch (err) {
      console.error('Error adding activity:', err);
      throw err;
    }
  };

  // Update existing activity
  const updateActivity = async (activityId, updates) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to update activities');
    }

    try {
      const activityRef = doc(db, 'dealActivities', activityId);
      await updateDoc(activityRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Error updating activity:', err);
      throw err;
    }
  };

  // Delete activity
  const deleteActivity = async (activityId) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to delete activities');
    }

    try {
      const activityRef = doc(db, 'dealActivities', activityId);
      await deleteDoc(activityRef);
    } catch (err) {
      console.error('Error deleting activity:', err);
      throw err;
    }
  };

  return {
    activities,
    loading,
    error,
    addActivity,
    updateActivity,
    deleteActivity
  };
}
